/**
 * Hard-gate allow-list (desktop). An unauthenticated desktop user may only
 * reach these routes; everything else redirects to /login. Exact entries match
 * the whole path; the trailing-slash entries are parametric prefixes.
 *
 *   /login /register  — auth surface
 *   /activate         — registration-code entry (has inline auth)
 *   /welcome          — first-run pre-auth landing
 *   /sign/  /verify/  — public signer + verification flows (token in the URL)
 */
const PUBLIC = ['/login', '/register', '/activate', '/welcome'];
const PUBLIC_PREFIXES = ['/sign/', '/verify/'];

export function isPublicAuthPath(pathname) {
  if (!pathname) return false;
  if (PUBLIC.includes(pathname)) return true;
  return PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
}

/**
 * True when a server-issued session is cached locally — i.e. the user has
 * signed in on this device before. This is the OFFLINE license proof: a
 * lifetime/paid customer who is offline (so Firebase can't restore
 * `auth.currentUser` — the token refresh needs network) must still be let
 * through the hard gate on the strength of this cached session. It's cleared
 * only by an explicit sign-out (`auth.signOut`) or an online session-kick
 * (`licenseCheck`'s 3-strike), so honoring it can't keep a truly-signed-out or
 * revoked user in — those paths remove it, and online the heartbeat re-validates.
 */
export function hasCachedSession() {
  try {
    const s = JSON.parse(localStorage.getItem('scrivar_session') || 'null');
    return Boolean(s && s.token);
  } catch {
    return false;
  }
}

/**
 * Hard-gate decision (desktop only). Redirect an unauthenticated desktop user
 * to /login UNLESS they're on a public auth path. "Authenticated" means ANY of:
 * a live React `user`, Firebase's synchronous `auth.currentUser`, a cached
 * session (offline grace — the key fix for offline lifetime customers), OR a
 * locally-verified offline license token (logout-capable activation — a
 * signed-out licensed device stays unlocked; see lib/licenseToken.js).
 *
 * @param {{desktop:boolean, loading:boolean, hasUser:boolean,
 *          hasFirebaseUser:boolean, hasSession:boolean,
 *          hasValidLicense:boolean, isPublic:boolean}} s
 * @returns {boolean} true → render <Navigate to="/login">
 */
export function shouldRedirectToLogin({ desktop, loading, hasUser, hasFirebaseUser, hasSession, hasValidLicense, isPublic }) {
  if (!desktop || loading || isPublic) return false;
  const authed = hasUser || hasFirebaseUser || hasSession || hasValidLicense;
  return !authed;
}
